import posthog from 'posthog-js'

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST = 'https://app.posthog.com'
const APP_ENV = import.meta.env.MODE || 'production'

// Event types for type safety
export interface PostHogEvents {
  session_start: {
    user_id?: string
    mode: 'clinic' | 'study'
    source_count: number
    is_mobile: boolean
  }

  query_fired: {
    user_id?: string
    mode: 'clinic' | 'study'
    query_length: number
    has_patient_context: boolean
    patient_age?: number
    patient_sex?: string
    patient_funding?: string
    source_count: number
    response_time_ms?: number
  }

  mode_switched: {
    user_id?: string
    from_mode: 'clinic' | 'study'
    to_mode: 'clinic' | 'study'
    trigger: 'manual' | 'mobile_banner'
  }

  source_toggled: {
    user_id?: string
    source_id: string
    source_tier: 'national' | 'state' | 'lhn' | 'specialty'
    enabled: boolean
    total_sources_enabled: number
  }

  feedback_submitted: {
    user_id?: string
    query_id: string
    feedback: 'up' | 'down'
    has_text: boolean
    mode: 'clinic' | 'study'
  }

  hcp_gate_completed: {
    user_id?: string
    time_taken_seconds: number
  }

  settings_updated: {
    user_id?: string
    field: 'state' | 'lhn' | 'default_mode' | 'sources'
    new_value: string | string[]
  }
}

// Initialize PostHog
export function initPostHog(): void {
  if (!POSTHOG_KEY) {
    console.warn('PostHog API key not configured')
    return
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    debug: APP_ENV === 'development',
    capture_pageview: true,
    capture_pageleave: true,

    // Privacy-focused configuration
    disable_session_recording: true, // No session recordings for healthcare
    respect_dnt: true,
    secure_cookie: true,

    // Reduce data collection
    property_blacklist: [
      '$current_url', // Don't track URLs that might contain sensitive info
      '$referrer'
    ]
  })

  console.log('PostHog initialized')
}

// Typed event tracking
export function trackEvent<T extends keyof PostHogEvents>(
  event: T,
  properties: PostHogEvents[T]
): void {
  if (!POSTHOG_KEY || APP_ENV === 'test') {
    console.log('PostHog event:', event, properties)
    return
  }

  try {
    posthog.capture(event, {
      ...properties,
      timestamp: new Date().toISOString(),
      app_version: '1.0.0', // Could be dynamic
      environment: APP_ENV
    })
  } catch (error) {
    console.error('PostHog tracking error:', error)
  }
}

// Identify user (call on login)
export function identifyUser(userId: string, properties?: {
  email?: string
  name?: string
  state?: string
  lhn?: string
  plan?: string
}): void {
  if (!POSTHOG_KEY || APP_ENV === 'test') {
    console.log('PostHog identify:', userId, properties)
    return
  }

  try {
    posthog.identify(userId, {
      ...properties,
      $set: {
        ...properties,
        last_login: new Date().toISOString()
      }
    })
  } catch (error) {
    console.error('PostHog identify error:', error)
  }
}

// Clear user data (call on logout)
export function resetUser(): void {
  if (!POSTHOG_KEY || APP_ENV === 'test') {
    console.log('PostHog reset')
    return
  }

  try {
    posthog.reset()
  } catch (error) {
    console.error('PostHog reset error:', error)
  }
}

// Helper to track session start
export function trackSessionStart(mode: 'clinic' | 'study', sourceCount: number, isMobile: boolean, userId?: string): void {
  trackEvent('session_start', {
    user_id: userId,
    mode,
    source_count: sourceCount,
    is_mobile: isMobile
  })
}

// Helper to track query with timing
export function trackQuery(params: {
  mode: 'clinic' | 'study'
  queryLength: number
  sourceCount: number
  responseTimeMs?: number
  userId?: string
}): void {
  trackEvent('query_fired', {
    user_id: params.userId,
    mode: params.mode,
    query_length: params.queryLength,
    has_patient_context: false,
    source_count: params.sourceCount,
    response_time_ms: params.responseTimeMs
  })
}

export default {
  init: initPostHog,
  track: trackEvent,
  identify: identifyUser,
  reset: resetUser,
  trackSessionStart,
  trackQuery
}